// src/components/shell/TopStrip.tsx
// Thin top identity strip per the style doctrine:
//   • app title (shrunk ~20% from the legacy header) + tagline inline
//   • activity banner bell — opens ActivityBannerPopover (notifications +
//     impersonation strip + ad alerts + achievements)
//   • optional `statusSlot` for per-mode status content
//
// Online dot, mode pill, settings gear, the x/x ads pill, and standalone
// sign-out are intentionally absent — they've moved to the Sidebar or have
// been folded into the activity banner (ad alerts → §4 / D2 rollup).
import type { ReactNode } from "react";
import { AttentionChrome } from "@/components/attention/AttentionBell";
import { TopStripBranchChip } from "@/components/shell/TopStripBranchChip";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useFireflyDisplayMode } from "@/hooks/useFireflyDisplayMode";
import { DOCK_AUDIENCE_ROLES } from "@/lib/activityDock/dockAudience";
import { useOperatingFront } from "@/components/operating-front/operatingFrontContext";

export type TopStripProps = {
  /** Per-mode status / banner content. Optional — pass null to leave blank. */
  statusSlot?: ReactNode;
  /** Optional extra controls rendered to the right of the bell (deprecated; prefer Sidebar). */
  trailingSlot?: ReactNode;
  /**
   * Forwarded to ActivityBannerPopover so the ad-alerts row inside the banner
   * can navigate the user into Ads Assistant ingestion. Wired by
   * LeadWorkspaceShell.
   */
  onOpenAdsIngestion?: () => void;
};

export function TopStrip({ statusSlot, trailingSlot, onOpenAdsIngestion }: TopStripProps) {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const { mode: fireflyDisplayMode } = useFireflyDisplayMode();
  const { brandName, appDisplayName, tagline } = useOrgSettings();
  const canSendTestOpsNotification = auth.hasRole(
    "admin",
    "owner",
    "dev",
    "platform_admin",
    "platform_dev"
  );
  // Mirror the server's editor-role gate (`canEdit` in opsPaperwork.ts) so the
  // composer's sender-lock checkbox only appears for roles that the API will
  // actually honor on POST notification-thread-create.
  const canUseSenderLock = auth.hasRole(
    "trainer",
    "marketing",
    "admin",
    "owner",
    "dev",
    "ads_guidance_editor",
    "platform_owner",
    "platform_admin",
    "platform_dev",
    "platform_trainer"
  );
  const canUseRequiresAck = canUseSenderLock;
  const operatingFront = useOperatingFront();

  return (
    // Mobile header geometry (dev sync notes 5ab15b65 + 1bd68297 + e365405e):
    //   • `pl-14` clears the fixed sandwich-menu button (h-10/w-10 at left-2 →
    //     spans x=8 to 48px); content starts at x=56 with an 8px gap so the
    //     menu glyph never overlaps the title leading edge.
    //   • `flex-nowrap` (mobile) keeps title + bell on the same row so the
    //     bell stays pinned top-right instead of wrapping to a bottom-right
    //     "smashed" position. The title group gets `min-w-0` + `truncate` so
    //     a long brand name ellipses rather than forcing wrap.
    //   • Title scales `text-2xl → sm:text-3xl → md:text-5xl` so the header
    //     height stays compact on phones (previously `text-4xl` even at 360px,
    //     which devoured ~25% of viewport height).
    //   • The brand/tagline strip is hidden below `sm:` to save vertical
    //     room; the title alone identifies the app on phones.
    // From `md:` upward the sandwich button is unmounted entirely and the
    // grid layout takes over, so the mobile-only overrides cleanly reset.
    <header className="w-full border-b border-gray-200 bg-white/60 backdrop-blur-sm pl-14 pr-3 sm:px-4 py-2 sm:py-3 min-h-[var(--shell-header-h)] dark:border-zinc-800 dark:bg-zinc-950">
      <div className="flex items-center gap-x-3 md:grid md:grid-cols-[1fr_auto_1fr]">
        <div className="hidden md:block" />
        <div className="flex min-w-0 flex-1 items-center gap-x-3 md:flex-none md:flex-wrap md:justify-self-center md:text-center">
          <h1
            className="brand-title-glow truncate text-2xl sm:text-3xl md:text-5xl font-black bg-clip-text text-transparent leading-tight pb-1 md:leading-snug"
            style={{
              backgroundImage: "linear-gradient(to right, var(--color-primary), var(--color-secondary))",
            }}
          >
            {appDisplayName}
          </h1>
          {brandName || tagline ? (
            <>
              <span
                aria-hidden
                className="hidden md:inline text-gray-300 dark:text-gray-600 text-xl leading-none select-none"
              >
                •
              </span>
              <p className="hidden sm:block text-xs sm:text-sm uppercase tracking-[0.2em] text-gray-500 break-words">
                {brandName}
                {tagline ? <span className="text-gray-400"> • {tagline}</span> : null}
              </p>
            </>
          ) : null}
          <TopStripBranchChip />
        </div>
        <div className="ml-auto md:ml-0 md:justify-self-end shrink-0 flex items-center gap-2">
          {operatingFront ? (
            <button
              type="button"
              onClick={operatingFront.returnToPicker}
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-800 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
            >
              Switch
            </button>
          ) : null}
          {auth.session ? (
            <AttentionChrome
              mode={fireflyDisplayMode}
              fireflyAvailable={Boolean(scopedOrgId) && auth.hasRole(...DOCK_AUDIENCE_ROLES)}
              allowTestNotification={canSendTestOpsNotification}
              allowSenderLock={canUseSenderLock}
              allowRequiresAck={canUseRequiresAck}
              onOpenAdsIngestion={onOpenAdsIngestion}
            />
          ) : null}
          {trailingSlot}
        </div>
      </div>
      {statusSlot ? <div className="mt-2">{statusSlot}</div> : null}
    </header>
  );
}
